import { App } from "vue";
import { signOut } from "@aws-amplify/auth";
import { routerPath } from "@/common/router";
import { localStorageClearAuthCache } from "@/common/localStorageUtil";

/**
 * アイドルタイムアウトプラグイン
 * ユーザー操作イベントで最終操作時刻を更新し、設定秒数経過でログアウト処理を呼ぶ
 * タイムアウト秒数はm_chainテーブルのidle_logout_secondsから取得する
 */
export default {
  install(app: App, options?: { router?: any }): void {
    let lastActivityTime = Date.now();
    let checkIntervalId: number | null = null;
    let isLoggedIn = false;
    let visibilityChangeHandler: (() => void) | null = null;
    // チェーン情報から取得したタイムアウト秒数を保持
    let idleTimeoutSeconds = 0;
    // 外部から渡されるログアウトコールバック
    let logoutCallback: (() => Promise<void>) | null = null;

    // 最終操作時刻をリセット
    const resetTimer = (): void => {
      lastActivityTime = Date.now();
    };

    // イベントリスナーを登録
    const registerEventListeners = (): void => {
      const events = ["click", "keydown", "scroll", "touchstart"];
      events.forEach((event) => {
        document.addEventListener(event, resetTimer, { passive: true });
      });
    };

    // イベントリスナーを解除
    const unregisterEventListeners = (): void => {
      const events = ["click", "keydown", "scroll", "touchstart"];
      events.forEach((event) => {
        document.removeEventListener(event, resetTimer);
      });
    };

    // アイドルタイムアウトを停止（既に停止済みの場合は何もしない）
    const stopIdleTimeout = (): void => {
      // 既に停止済みの場合は重複ログを防ぐため早期リターン
      if (!isLoggedIn && checkIntervalId === null) {
        return;
      }

      isLoggedIn = false;

      if (checkIntervalId !== null) {
        window.clearInterval(checkIntervalId);
        checkIntervalId = null;
      }

      // visibilitychange リスナーを解除
      if (visibilityChangeHandler !== null) {
        document.removeEventListener(
          "visibilitychange",
          visibilityChangeHandler
        );
        visibilityChangeHandler = null;
      }

      unregisterEventListeners();
      // タイムアウト秒数をリセット
      idleTimeoutSeconds = 0;
      // ログアウトコールバックをリセット
      logoutCallback = null;
      console.log("[IdleTimeout] Stopped idle timeout monitoring");
    };

    // ログアウト処理
    const performLogout = async (router: any): Promise<void> => {
      // 既にログアウト処理中または停止済みの場合はスキップ
      if (!isLoggedIn) {
        return;
      }

      console.log("[IdleTimeout] Performing logout due to inactivity");

      // 先にアイドル監視を停止（ログアウトの重複実行を防ぐ）
      const callback = logoutCallback;
      stopIdleTimeout();

      // 外部から渡されたログアウトコールバックがある場合はそちらを使用
      if (callback) {
        await callback();
        return;
      }

      // コールバックが未設定の場合はデフォルトのログアウト処理
      try {
        await signOut();
      } finally {
        // ローカルストレージをクリア（"userData" に統一済み。旧キー ".userData" も互換性のため削除）
        localStorageClearAuthCache();

        // ログイン画面へリダイレクト
        if (router && router.currentRoute.value.path !== routerPath.login) {
          router.push({ path: routerPath.login });
        }
      }
    };

    // タイムアウトチェック
    const checkIdleTimeout = (router: any): void => {
      // ログイン状態でない場合はスキップ
      if (!isLoggedIn) {
        return;
      }

      // 0以下の場合は無効
      if (idleTimeoutSeconds <= 0) {
        return;
      }

      const idleTime = Date.now() - lastActivityTime;
      const timeoutMs = idleTimeoutSeconds * 1000;

      if (idleTime > timeoutMs) {
        // performLogout は async のため、未処理の Promise rejection を防ぐ
        performLogout(router).catch((err) => {
          console.error("[IdleTimeout] Error during logout", err);
        });
      }
    };

    // タブ復帰時のチェック
    const handleVisibilityChange = (router: any): void => {
      if (document.visibilityState === "visible" && isLoggedIn) {
        checkIdleTimeout(router);
      }
    };

    /**
     * アイドルタイムアウトを開始
     * @param router Vueルーター
     * @param timeoutSeconds タイムアウト秒数（m_chainのidle_logout_secondsから取得した値）
     * @param onLogout ログアウト時に呼び出すコールバック（App.vue の onClickLogout を渡す）
     */
    const startIdleTimeout = (
      router: any,
      timeoutSeconds: number,
      onLogout?: () => Promise<void>
    ): void => {
      if (checkIntervalId !== null) {
        return; // 既に開始済み
      }

      // タイムアウト秒数を設定
      idleTimeoutSeconds = timeoutSeconds || 0;

      // 0以下の場合は監視を開始しない
      if (idleTimeoutSeconds <= 0) {
        console.log(
          "[IdleTimeout] Idle timeout is disabled (timeoutSeconds <= 0)"
        );
        return;
      }

      // ログアウトコールバックを保持
      logoutCallback = onLogout ?? null;

      isLoggedIn = true;
      lastActivityTime = Date.now();
      registerEventListeners();

      // 1分ごとにチェック
      checkIntervalId = window.setInterval(() => {
        if (isLoggedIn) {
          checkIdleTimeout(router);
        }
      }, 60000);

      // visibilitychange イベントを登録（変数に保持して後で解除可能にする）
      visibilityChangeHandler = () => handleVisibilityChange(router);
      document.addEventListener("visibilitychange", visibilityChangeHandler);

      console.log(
        `[IdleTimeout] Started idle timeout monitoring (${idleTimeoutSeconds} seconds)`
      );
    };

    // Vue インスタンスメソッドとして登録
    app.config.globalProperties.$idleTimeout = {
      /**
       * アイドルタイムアウト監視を開始
       * @param router Vueルーター
       * @param timeoutSeconds タイムアウト秒数（m_chainのidle_logout_secondsから取得した値）
       * @param onLogout ログアウト時に呼び出すコールバック（App.vue の onClickLogout を渡す）
       */
      start: (
        router: any,
        timeoutSeconds: number,
        onLogout?: () => Promise<void>
      ) => startIdleTimeout(router, timeoutSeconds, onLogout),
      stop: () => stopIdleTimeout(),
      reset: () => resetTimer(),
      getTimeoutSeconds: () => idleTimeoutSeconds,
    };
  },
};
